import { isAnyObject } from "@-/com.npmjs.is-what";
import { mergeSoftDeleteWhere } from "#merge-soft-delete-where";
import type { RelationalFindConfig } from "#relational-query";

// Wraps `raw.query` so `findMany` / `findFirst` inject the soft-delete predicate
// at the top level for tables selected by `shouldApplySoftDelete`. Nested
// `with: { ... }` clauses are filtered by predefined `where` on relations
// instead, so this wrapper doesn't need to recurse.
export function wrapRelationalQuery<TRawQuery extends object>(
  rawQuery: TRawQuery,
  options: {
    assertQuery: (value: unknown) => value is TRawQuery;
    proxyBuildErrorMessage: string;
    shouldApplySoftDelete: (tableName: string) => boolean;
  },
): TRawQuery {
  const proxied: Record<string, unknown> = {};
  for (const tableName of Object.keys(rawQuery)) {
    const tableQuery = Reflect.get(rawQuery, tableName);
    if (!isAnyObject(tableQuery)) {
      proxied[tableName] = tableQuery;
      continue;
    }
    const applySoftDelete = options.shouldApplySoftDelete(tableName);
    const originalFindFirst = Reflect.get(tableQuery, "findFirst");
    const originalFindMany = Reflect.get(tableQuery, "findMany");
    proxied[tableName] = {
      ...tableQuery,
      ...(typeof originalFindFirst === "function"
        ? {
            findFirst: (config?: RelationalFindConfig) =>
              applySoftDelete
                ? originalFindFirst.call(tableQuery, mergeSoftDeleteWhere(config))
                : originalFindFirst.call(tableQuery, config),
          }
        : {}),
      ...(typeof originalFindMany === "function"
        ? {
            findMany: (config?: RelationalFindConfig) =>
              applySoftDelete
                ? originalFindMany.call(tableQuery, mergeSoftDeleteWhere(config))
                : originalFindMany.call(tableQuery, config),
          }
        : {}),
    };
  }
  if (!options.assertQuery(proxied)) {
    throw new TypeError(options.proxyBuildErrorMessage);
  }
  return proxied;
}
